import { useEffect, useState } from 'react';

const getServerBase = () =>
  import.meta.env.MODE === 'production' ? '/api' : 'http://localhost:4000';

const getAuthToken = () => localStorage.getItem('loginToken') || '';

export const getAttachmentRequestUrl = (attachmentPath: string): string => {
  const serverBase = getServerBase();

  if (/^https?:\/\//.test(attachmentPath)) {
    return attachmentPath;
  }

  if (attachmentPath.startsWith('/attachments/')) {
    return `${serverBase}${attachmentPath}`;
  }

  return `${serverBase}/attachments/${attachmentPath.replace(/^\/+/, '')}`;
};

export const fetchAttachmentBlob = async (attachmentPath: string) => {
  const response = await fetch(getAttachmentRequestUrl(attachmentPath), {
    headers: {
      Authorization: getAuthToken(),
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch attachment (${response.status})`);
  }

  return response.blob();
};

export const createAttachmentObjectUrl = async (
  attachmentPath: string
): Promise<string> => URL.createObjectURL(await fetchAttachmentBlob(attachmentPath));

export const revokeAttachmentObjectUrl = (objectUrl?: string | null) => {
  if (objectUrl) {
    URL.revokeObjectURL(objectUrl);
  }
};

const revokeLater = (objectUrl: string, delayMs = 60_000) => {
  globalThis.setTimeout(() => {
    URL.revokeObjectURL(objectUrl);
  }, delayMs);
};

export const downloadAttachment = async (
  attachmentPath: string,
  filename: string
): Promise<void> => {
  const objectUrl = await createAttachmentObjectUrl(attachmentPath);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  revokeLater(objectUrl);
};

export const openAttachmentInNewTab = async (
  attachmentPath: string
): Promise<void> => {
  // Open the tab synchronously within the click gesture so popup blockers
  // allow it, then navigate it once the blob is fetched. `noopener` would
  // make window.open return null, forcing an async open that Firefox blocks.
  const newWindow = window.open('', '_blank');

  try {
    const objectUrl = await createAttachmentObjectUrl(attachmentPath);

    if (newWindow) {
      newWindow.location.href = objectUrl;
    } else {
      window.open(objectUrl, '_blank');
    }

    revokeLater(objectUrl);
  } catch (error) {
    newWindow?.close();
    throw error;
  }
};

export const useAttachmentObjectUrl = (
  attachmentPath: string | null | undefined,
  enabled = true
) => {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!attachmentPath || !enabled) {
      setObjectUrl(prev => {
        revokeAttachmentObjectUrl(prev);
        return null;
      });
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;
    let nextObjectUrl: string | null = null;

    setLoading(true);
    setError(null);

    void createAttachmentObjectUrl(attachmentPath)
      .then(url => {
        if (cancelled) {
          revokeAttachmentObjectUrl(url);
          return;
        }

        nextObjectUrl = url;
        setObjectUrl(prev => {
          revokeAttachmentObjectUrl(prev);
          return url;
        });
      })
      .catch(err => {
        if (cancelled) {
          return;
        }

        setObjectUrl(prev => {
          revokeAttachmentObjectUrl(prev);
          return null;
        });
        setError(err instanceof Error ? err.message : 'Failed to load attachment');
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
      revokeAttachmentObjectUrl(nextObjectUrl);
    };
  }, [attachmentPath, enabled]);

  return { objectUrl, loading, error };
};